/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Comparison,
  Condition,
  ConditionGroup,
  getProperty,
  getPropertyOperatorsForType,
  Operator,
  PropertyType,
  PropertyValueType,
  Rule,
} from './models';

export type ConditionEvaluationValue = PropertyValueType | PropertyValueType[] | null;

export interface EvaluatedConditionResult {
  kind: 'condition';
  outcome: 'passed' | 'failed' | 'skipped';
  passed: boolean;
  skipped: boolean;
  propertyId: string;
  propertyLabel: string;
  operator: Operator;
  type: PropertyType;
  actualValue: ConditionEvaluationValue;
  expectedValue: ConditionEvaluationValue;
  valuePropertyId?: string | null;
}

export interface EvaluatedConditionGroupResult {
  kind: 'group';
  outcome: 'passed' | 'failed';
  passed: boolean;
  skipped: false;
  comparison: Comparison;
  conditions: ConditionEvaluationResult[];
}

export type ConditionEvaluationResult = EvaluatedConditionResult | EvaluatedConditionGroupResult;

/** New text negation operators only: NULL/empty LHS passes. Legacy `!=` and `does not start with` are unchanged. */
const TEXT_NEGATION_OPERATORS_NULL_PASSES: ReadonlySet<Operator> = new Set([
  Operator.NOT_CONTAINS,
  Operator.NOT_ENDS_WITH,
  Operator.NOT_IN,
]);

function isTextPropertyValueAbsent(propertyValue: PropertyValueType | null | undefined): boolean {
  return (
    propertyValue === null || propertyValue === undefined || propertyValue === '' || typeof propertyValue !== 'string'
  );
}

function parseCsvList(raw: string): string[] {
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

function joinTextArrayValues(arr: unknown[]): string {
  return (Array.isArray(arr) ? arr : [])
    .filter((v): v is string => typeof v === 'string')
    .map(v => v.trim())
    .filter(v => v.length > 0)
    .join(',');
}

function normalizeEvaluationValue(value: unknown): ConditionEvaluationValue {
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value)) {
    return value.filter(
      (entry): entry is PropertyValueType =>
        entry instanceof Date || ['string', 'number', 'boolean'].includes(typeof entry)
    );
  }

  if (value instanceof Date || ['string', 'number', 'boolean'].includes(typeof value)) {
    return value as PropertyValueType;
  }

  return String(value) as string;
}

function buildConditionResult<T extends PropertyValueType>(
  condition: Condition<T>,
  actualValue: unknown,
  expectedValue: unknown,
  passed: boolean,
  outcome: EvaluatedConditionResult['outcome'] = passed ? 'passed' : 'failed'
): EvaluatedConditionResult {
  const property = getProperty(condition.propertyId);

  return {
    kind: 'condition',
    outcome,
    passed,
    skipped: outcome === 'skipped',
    propertyId: condition.propertyId,
    propertyLabel: property?.name ?? condition.propertyId,
    operator: condition.operator,
    type: condition.type,
    actualValue: normalizeEvaluationValue(actualValue),
    expectedValue: normalizeEvaluationValue(expectedValue),
    valuePropertyId: condition.valuePropertyId ?? null,
  };
}

function compareOrdered(left: string | number, operator: Operator, right: string | number): boolean {
  switch (operator) {
    case Operator.GREATER_THAN:
      return left > right;
    case Operator.GREATER_THAN_EQUALS:
      return left >= right;
    case Operator.LESS_THAN:
      return left < right;
    case Operator.LESS_THAN_EQUALS:
      return left <= right;
    default:
      return false;
  }
}

function evaluateTextStringOperator(operator: Operator, propertyValue: string, value: string): boolean {
  const pl = propertyValue.toLowerCase();
  const vl = value.toLowerCase();
  switch (operator) {
    case Operator.CONTAINS:
      return pl.includes(vl);
    case Operator.NOT_CONTAINS:
      return !pl.includes(vl);
    case Operator.EQUALS:
      return pl === vl;
    case Operator.NOT_EQUALS:
      return pl !== vl;
    case Operator.STARTS_WITH:
      return pl.startsWith(vl);
    case Operator.NOT_STARTS_WITH:
      return !pl.startsWith(vl);
    case Operator.ENDS_WITH:
      return pl.endsWith(vl);
    case Operator.NOT_ENDS_WITH:
      return !pl.endsWith(vl);
    case Operator.IN: {
      const options = parseCsvList(value);
      if (!options.length) return false;
      return options.some(o => pl === o.toLowerCase());
    }
    case Operator.NOT_IN: {
      const options = parseCsvList(value);
      if (!options.length) return true;
      return !options.some(o => pl === o.toLowerCase());
    }
    default:
      // Lexicographic comparison for text-stored numerics, etc.
      return compareOrdered(pl, operator, vl);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPropertyValue(propertyPath: string, propertyValues: Record<string, any>): PropertyValueType | null {
  if (!propertyValues) return null;

  const valueKeys = Object.keys(propertyValues);

  for (const key of valueKeys) {
    if (key === propertyPath) {
      return propertyValues[propertyPath];
    } else if (propertyPath.startsWith(key)) {
      return getPropertyValue(propertyPath.replace(key + '.', ''), propertyValues[key]);
    }
  }

  return null;
}

function evaluateNumberCondition(
  condition: Condition<number>,
  propertyValues: Record<string, Record<string, PropertyValueType>>
): EvaluatedConditionResult {
  if (!getPropertyOperatorsForType(PropertyType.NUMBER).includes(condition.operator)) {
    return buildConditionResult(condition, null, condition.value, false);
  }

  const propertyValue = getPropertyValue(condition.propertyId, propertyValues);

  if (condition.operator === Operator.IS_NULL) {
    const passed = propertyValue === null || propertyValue === undefined || propertyValue === '';
    return buildConditionResult(condition, propertyValue, null, passed);
  }

  if (condition.operator === Operator.IS_NOT_NULL) {
    const passed = propertyValue !== null && propertyValue !== undefined && propertyValue !== '';
    return buildConditionResult(condition, propertyValue, null, passed);
  }

  if (propertyValue === null || propertyValue === undefined || typeof propertyValue !== 'number') {
    return buildConditionResult(condition, propertyValue, condition.value, false);
  }

  let value = condition.value;

  if (condition.valuePropertyId) {
    const conditionValue = getPropertyValue(condition.valuePropertyId, propertyValues);
    if (conditionValue === null || conditionValue === undefined || typeof conditionValue !== 'number') {
      return buildConditionResult(condition, propertyValue, conditionValue, false);
    }
    value = conditionValue;
  }

  if (value === null || value === undefined) {
    return buildConditionResult(condition, propertyValue, value, false);
  }

  let passed: boolean;
  switch (condition.operator) {
    case Operator.EQUALS:
      passed = propertyValue === value;
      break;
    case Operator.NOT_EQUALS:
      passed = propertyValue !== value;
      break;
    default:
      passed = compareOrdered(propertyValue, condition.operator, value);
      break;
  }

  return buildConditionResult(condition, propertyValue, value, passed);
}

function evaluateTextArrayCondition(
  condition: Condition<string>,
  propertyValues: Record<string, Record<string, PropertyValueType>>,
  _propertyValue: unknown[]
): EvaluatedConditionResult {
  let value = condition.value;

  if (condition.valuePropertyId) {
    const conditionValue = getPropertyValue(condition.valuePropertyId, propertyValues);
    value = typeof conditionValue === 'string' ? conditionValue : null;
  }

  const arr = _propertyValue && Array.isArray(_propertyValue) ? _propertyValue : [];
  const propertyValueArray = arr
    .filter((value): value is string => !!value && typeof value === 'string')
    .map(value => value.toLowerCase().trim())
    .filter(Boolean)
    .sort();

  const comparisonValueArray = (value ?? '')
    .split(',')
    .map(value => value.toLowerCase().trim())
    .filter(Boolean)
    .sort();

  let passed: boolean;
  switch (condition.operator) {
    case Operator.EQUALS:
      // Arrays match exactly
      passed = propertyValueArray.join('') === comparisonValueArray.join('');
      break;

    case Operator.NOT_EQUALS:
      // Property value does not contain any value in comparison value
      passed = !comparisonValueArray.some(value => propertyValueArray.includes(value));
      break;

    case Operator.CONTAINS:
      // Property value contains all values in comparison value
      passed = comparisonValueArray.every(value => propertyValueArray.includes(value));
      break;

    case Operator.IS_NULL:
      // Property value is empty
      passed = propertyValueArray.length === 0;
      break;

    case Operator.IS_NOT_NULL:
      // Property value is not empty
      passed = propertyValueArray.length > 0;
      break;

    case Operator.STARTS_WITH:
    case Operator.NOT_STARTS_WITH:
    case Operator.ENDS_WITH:
    case Operator.NOT_ENDS_WITH:
    case Operator.NOT_CONTAINS:
    case Operator.IN:
    case Operator.NOT_IN: {
      const joined = joinTextArrayValues(arr);
      if (TEXT_NEGATION_OPERATORS_NULL_PASSES.has(condition.operator) && isTextPropertyValueAbsent(joined)) {
        return buildConditionResult(condition, arr, value, true);
      }
      if (isTextPropertyValueAbsent(joined)) return buildConditionResult(condition, arr, value, false);
      if (value === null || value === undefined) return buildConditionResult(condition, arr, value, false);
      passed = evaluateTextStringOperator(condition.operator, joined, value);
      break;
    }

    default:
      passed = false;
      break;
  }

  return buildConditionResult(condition, arr, value, passed);
}

function evaluateTextCondition(
  condition: Condition<string>,
  propertyValues: Record<string, Record<string, PropertyValueType>>
): EvaluatedConditionResult {
  if (!getPropertyOperatorsForType(PropertyType.TEXT).includes(condition.operator)) {
    return buildConditionResult(condition, null, condition.value, false);
  }

  const propertyValue = getPropertyValue(condition.propertyId, propertyValues);

  if (Array.isArray(propertyValue)) {
    return evaluateTextArrayCondition(condition, propertyValues, propertyValue);
  }

  if (condition.operator === Operator.IS_NULL) {
    const passed = propertyValue === null || propertyValue === undefined || propertyValue === '';
    return buildConditionResult(condition, propertyValue, null, passed);
  }

  if (condition.operator === Operator.IS_NOT_NULL) {
    const passed = propertyValue !== null && propertyValue !== undefined && propertyValue !== '';
    return buildConditionResult(condition, propertyValue, null, passed);
  }

  let value = condition.value;

  if (condition.valuePropertyId) {
    const conditionValue = getPropertyValue(condition.valuePropertyId, propertyValues);
    if (conditionValue === null || conditionValue === undefined || typeof conditionValue !== 'string') {
      value = null;
    } else {
      value = conditionValue;
    }
  }

  if (TEXT_NEGATION_OPERATORS_NULL_PASSES.has(condition.operator) && isTextPropertyValueAbsent(propertyValue)) {
    return buildConditionResult(condition, propertyValue, value, true);
  }

  if (isTextPropertyValueAbsent(propertyValue)) {
    return buildConditionResult(condition, propertyValue, value, false);
  }

  const propertyStr = propertyValue as string;

  if (value === null || value === undefined) {
    return buildConditionResult(condition, propertyStr, value, false);
  }

  const passed = evaluateTextStringOperator(condition.operator, propertyStr, value);
  return buildConditionResult(condition, propertyStr, value, passed);
}

function evaluateBooleanCondition(
  condition: Condition<boolean>,
  propertyValues: Record<string, Record<string, PropertyValueType>>
): EvaluatedConditionResult {
  if (!getPropertyOperatorsForType(PropertyType.BOOLEAN).includes(condition.operator)) {
    return buildConditionResult(condition, null, condition.value, false);
  }
  const propertyValue = getPropertyValue(condition.propertyId, propertyValues);

  if (condition.operator === Operator.IS_NULL) {
    const passed = propertyValue === null || propertyValue === undefined || propertyValue === '';
    return buildConditionResult(condition, propertyValue, null, passed);
  }

  if (condition.operator === Operator.IS_NOT_NULL) {
    const passed = propertyValue !== null && propertyValue !== undefined && propertyValue !== '';
    return buildConditionResult(condition, propertyValue, null, passed);
  }

  if (propertyValue === null || propertyValue === undefined || typeof propertyValue !== 'boolean') {
    return buildConditionResult(condition, propertyValue, condition.value, false);
  }

  let value = condition.value;

  if (condition.valuePropertyId) {
    const conditionValue = getPropertyValue(condition.valuePropertyId, propertyValues);
    if (conditionValue === null || conditionValue === undefined || typeof conditionValue !== 'boolean') {
      return buildConditionResult(condition, propertyValue, conditionValue, false);
    }
    value = conditionValue as boolean;
  }

  if (value === null || value === undefined) {
    return buildConditionResult(condition, propertyValue, value, false);
  }

  const passed = condition.operator === Operator.EQUALS ? propertyValue === value : propertyValue !== value;
  return buildConditionResult(condition, propertyValue, value, passed);
}

function evaluateDateCondition(
  condition: Condition<Date>,
  propertyValues: Record<string, Record<string, PropertyValueType>>
): EvaluatedConditionResult {
  if (!getPropertyOperatorsForType(PropertyType.DATE).includes(condition.operator)) {
    return buildConditionResult(condition, null, condition.value, false);
  }
  try {
    let propertyValue = getPropertyValue(condition.propertyId, propertyValues);
    const rawPropertyValue = propertyValue;

    if (condition.operator === Operator.IS_NULL) {
      const passed = propertyValue === null || propertyValue === undefined || propertyValue === '';
      return buildConditionResult(condition, propertyValue, null, passed);
    }

    if (condition.operator === Operator.IS_NOT_NULL) {
      const passed = propertyValue !== null && propertyValue !== undefined && propertyValue !== '';
      return buildConditionResult(condition, propertyValue, null, passed);
    }

    const type = typeof propertyValue;
    if (
      propertyValue === null ||
      propertyValue === undefined ||
      (type !== 'string' && type !== 'number' && type !== 'object')
    )
      return buildConditionResult(condition, propertyValue, condition.value, false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    propertyValue = new Date(propertyValue as any);

    let value = condition.value;

    if (condition.valuePropertyId) {
      const conditionValue = getPropertyValue(condition.valuePropertyId, propertyValues);
      const conditionType = typeof conditionValue;
      if (
        conditionValue === null ||
        conditionValue === undefined ||
        (conditionType !== 'string' && conditionType !== 'number' && conditionType !== 'object')
      )
        return buildConditionResult(condition, rawPropertyValue, conditionValue, false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value = new Date(conditionValue as any);
    }

    if (value === null || value === undefined) {
      return buildConditionResult(condition, rawPropertyValue, value, false);
    }

    const propertyValueTime = (propertyValue as Date).getTime();
    const conditionValueTime = value.getTime();

    let passed: boolean;
    switch (condition.operator) {
      case Operator.EQUALS:
        passed = propertyValueTime === conditionValueTime;
        break;
      case Operator.NOT_EQUALS:
        passed = propertyValueTime !== conditionValueTime;
        break;
      default:
        passed = compareOrdered(propertyValueTime, condition.operator, conditionValueTime);
        break;
    }

    return buildConditionResult(condition, rawPropertyValue, value, passed);
  } catch (err) {
    console.error(err);
    return buildConditionResult(condition, null, condition.value, false);
  }
}

function evaluateConditionDetailed<T extends PropertyValueType>(
  condition: Condition<T>,
  propertyValues: Record<string, Record<string, PropertyValueType>>
): EvaluatedConditionResult {
  switch (condition.type) {
    case PropertyType.TEXT:
      return evaluateTextCondition(condition as Condition<string>, propertyValues);
    case PropertyType.NUMBER:
      return evaluateNumberCondition(condition as Condition<number>, propertyValues);
    case PropertyType.BOOLEAN:
      return evaluateBooleanCondition(condition as Condition<boolean>, propertyValues);
    default:
      return evaluateDateCondition(condition as Condition<Date>, propertyValues);
  }
}

function evaluateCondition<T extends PropertyValueType>(
  condition: Condition<T>,
  propertyValues: Record<string, Record<string, PropertyValueType>>
): boolean {
  return evaluateConditionDetailed(condition, propertyValues).passed;
}

/** Property prefixes known at sales/checkout time (session customer + distributor). */
const SALES_TIME_PARTY_PROPERTY_PREFIXES = ['distributor.', 'customer.'] as const;

export function isSalesTimePartyPropertyId(propertyId: string): boolean {
  return SALES_TIME_PARTY_PROPERTY_PREFIXES.some(prefix => propertyId.startsWith(prefix));
}

/**
 * True when any leaf condition's `propertyId` references distributor/customer.
 * Used at sales time so asset (and other) conditions are not evaluated before asset data exists.
 */
export function hasSalesTimePartyConditions(group: ConditionGroup | null | undefined): boolean {
  if (!group?.conditions?.length) return false;

  for (const item of group.conditions) {
    if ('propertyId' in item) {
      if (isSalesTimePartyPropertyId(item.propertyId)) return true;
    } else if (hasSalesTimePartyConditions(item)) {
      return true;
    }
  }

  return false;
}

export function evaluateConditionGroupDetailed(
  group: ConditionGroup,
  propertyValues: Record<string, Record<string, PropertyValueType>>,
  filter?: (propertyKey: string) => boolean
): EvaluatedConditionGroupResult {
  const conditions = group.conditions.map((conditionOrGroup): ConditionEvaluationResult => {
    if ('propertyId' in conditionOrGroup) {
      if (filter && !filter(conditionOrGroup.propertyId)) {
        return buildConditionResult(conditionOrGroup, null, conditionOrGroup.value, false, 'skipped');
      }

      return evaluateConditionDetailed(conditionOrGroup, propertyValues);
    }

    return evaluateConditionGroupDetailed(conditionOrGroup, propertyValues, filter);
  });

  const passed =
    group.comparison === Comparison.AND
      ? conditions.every(condition => condition.passed)
      : conditions.some(condition => condition.passed);

  return {
    kind: 'group',
    outcome: passed ? 'passed' : 'failed',
    passed,
    skipped: false,
    comparison: group.comparison,
    conditions,
  };
}

function evaluateConditionGroupBoolean(
  group: ConditionGroup,
  propertyValues: Record<string, Record<string, PropertyValueType>>,
  filter?: (propertyKey: string) => boolean
): boolean {
  if (group.comparison === Comparison.AND) {
    for (const conditionOrGroup of group.conditions) {
      const passed =
        'propertyId' in conditionOrGroup
          ? filter && !filter(conditionOrGroup.propertyId)
            ? false
            : evaluateCondition(conditionOrGroup, propertyValues)
          : evaluateConditionGroupBoolean(conditionOrGroup, propertyValues, filter);
      if (!passed) return false;
    }
    return true;
  }

  for (const conditionOrGroup of group.conditions) {
    const passed =
      'propertyId' in conditionOrGroup
        ? filter && !filter(conditionOrGroup.propertyId)
          ? false
          : evaluateCondition(conditionOrGroup, propertyValues)
        : evaluateConditionGroupBoolean(conditionOrGroup, propertyValues, filter);
    if (passed) return true;
  }
  return false;
}

export function evaluateConditionGroup(
  group: ConditionGroup,
  propertyValues: Record<string, Record<string, PropertyValueType>>,
  filter?: (propertyKey: string) => boolean
): boolean {
  return evaluateConditionGroupBoolean(group, propertyValues, filter);
}

export function evaluateRule<T>(rule: Rule<T>, propertyValues: Record<string, Record<string, PropertyValueType>>): T {
  for (const block of rule.blocks) {
    const result = evaluateConditionGroup(block.condition, propertyValues);
    let value = block.value;

    if (block.valuePropertyPath) {
      value = getPropertyValue(block.valuePropertyPath, propertyValues) as T;
    }
    if (result) return value;
  }
  let defaultValue = rule.defaultValue;

  if (rule.valuePropertyPath) {
    defaultValue = getPropertyValue(rule.valuePropertyPath, propertyValues) as T;
  }

  return defaultValue;
}
